import {
  ArrowBack as ArrowBackIcon,
  Close as CloseIcon,
  DeleteOutline as DeleteIcon,
  Event as EventIcon,
  OpenInNew as OpenInNewIcon,
  Description as ReportIcon,
  Schedule as ScheduleIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  LinearProgress,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { formatDistanceToNow } from "date-fns";
import { MuiMarkdown } from "mui-markdown";
import { useSnackbar } from "notistack";
import { useEffect, useMemo, useState } from "react";
import {
  useArgusSessionReport,
  useDeleteArgusSchedule,
  useListArgusScheduleRuns,
  useListArgusSchedules,
} from "../../hooks/argus";
import { RADIUS_MD } from "../../theme/theme";
import type { ArgusSchedule, ArgusScheduleRun, ArgusScheduleStatus } from "../../types/argus";
import { EmptyState, LoadingBox } from "../ui";

interface ArgusSchedulesDialogProps {
  open: boolean;
  onClose: () => void;
  instanceId: string | undefined;
  onNavigateToSession: (sessionId: string) => void;
}

// Fixed inner dimensions so both tabs — and both schedule/run detail modes —
// render at the same visual weight regardless of whether the list is empty.
const CONTENT_MIN_HEIGHT = 620;
const LIST_WIDTH = 320;

const MARKDOWN_HEADING_SX = (fontSize: string, mt: number) => ({
  fontSize,
  fontWeight: 600,
  mt,
  mb: 0.75,
  lineHeight: 1.35,
});
const MARKDOWN_OVERRIDES = {
  h1: {
    component: Typography,
    props: { sx: MARKDOWN_HEADING_SX("1.05rem", 2) },
  },
  h2: {
    component: Typography,
    props: { sx: MARKDOWN_HEADING_SX("1rem", 1.75) },
  },
  h3: {
    component: Typography,
    props: { sx: MARKDOWN_HEADING_SX("0.95rem", 1.5) },
  },
  h4: {
    component: Typography,
    props: { sx: MARKDOWN_HEADING_SX("0.9rem", 1.25) },
  },
  h5: {
    component: Typography,
    props: { sx: MARKDOWN_HEADING_SX("0.875rem", 1) },
  },
  h6: {
    component: Typography,
    props: { sx: MARKDOWN_HEADING_SX("0.875rem", 1) },
  },
  p: {
    component: Typography,
    props: {
      variant: "body2",
      sx: { mb: 1, lineHeight: 1.6, whiteSpace: "pre-wrap" },
    },
  },
  li: {
    component: Typography,
    props: { variant: "body2", component: "li", sx: { mb: 0.25 } },
  },
};

function formatRelative(iso: string | null): string {
  if (!iso) return "never";
  try {
    return `${formatDistanceToNow(new Date(iso))} ago`;
  } catch {
    return iso;
  }
}

function formatAbsolute(iso: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function scheduleTrigger(s: ArgusSchedule): {
  kind: "cron" | "one-shot";
  label: string;
  short: string;
} {
  if (s.cron_expression) {
    return { kind: "cron", label: s.cron_expression, short: "Recurring" };
  }
  return {
    kind: "one-shot",
    label: formatAbsolute(s.run_at),
    short: "One-shot",
  };
}

function runStatusColor(status: string): "default" | "primary" | "success" | "error" | "warning" {
  const s = status.toLowerCase();
  if (s === "running") return "primary";
  if (s === "completed" || s === "succeeded" || s === "success") return "success";
  if (s === "failed" || s === "error" || s === "errored") return "error";
  if (s === "aborted" || s === "cancelled" || s === "canceled") return "warning";
  return "default";
}

function runDurationLabel(run: ArgusScheduleRun): string {
  if (!run.ended_at) return "—";
  try {
    const ms = new Date(run.ended_at).getTime() - new Date(run.started_at).getTime();
    if (ms < 1000) return `${ms}ms`;
    const s = Math.floor(ms / 1000);
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    const rem = s % 60;
    return `${m}m ${rem}s`;
  } catch {
    return "—";
  }
}

export function ArgusSchedulesDialog({
  open,
  onClose,
  instanceId,
  onNavigateToSession,
}: ArgusSchedulesDialogProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [tab, setTab] = useState<ArgusScheduleStatus>("active");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const activeQuery = useListArgusSchedules(instanceId, "active", {
    enabled: open,
  });
  const pastQuery = useListArgusSchedules(instanceId, "deleted", {
    enabled: open && tab === "deleted",
  });

  const currentQuery = tab === "active" ? activeQuery : pastQuery;

  // Combine instance-scoped + org-wide schedules into a single ordered list.
  // We keep the group labels implicit — showing a single sorted column reads
  // better than two short stacks when there are only a handful of entries.
  const allSchedules: ArgusSchedule[] = useMemo(
    () => [...currentQuery.forInstance, ...currentQuery.orgWide],
    [currentQuery.forInstance, currentQuery.orgWide],
  );

  const filteredSchedules = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allSchedules;
    return allSchedules.filter((s) => {
      const haystack = `${s.name} ${s.prompt ?? ""} ${s.cron_expression ?? ""}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [allSchedules, query]);

  const activeCount = (activeQuery.forInstance?.length ?? 0) + (activeQuery.orgWide?.length ?? 0);
  const pastCount = pastQuery.data ? pastQuery.forInstance.length + pastQuery.orgWide.length : null;

  const selectedSchedule: ArgusSchedule | null = useMemo(() => {
    if (!selectedId) return null;
    return allSchedules.find((s) => s.id === selectedId) ?? null;
  }, [selectedId, allSchedules]);

  const runsQuery = useListArgusScheduleRuns(selectedId ?? undefined, {
    enabled: open && !!selectedId,
  });
  const runs: ArgusScheduleRun[] = runsQuery.data?.runs ?? [];

  const selectedRun: ArgusScheduleRun | null = useMemo(() => {
    if (!selectedRunId) return null;
    return runs.find((r) => r.id === selectedRunId) ?? null;
  }, [selectedRunId, runs]);

  const deleteMutation = useDeleteArgusSchedule();

  // Reset all transient state on close so reopening feels fresh.
  useEffect(() => {
    if (!open) {
      setSelectedId(null);
      setSelectedRunId(null);
      setConfirmDeleteOpen(false);
      setQuery("");
      setTab("active");
    }
  }, [open]);

  // If the selected schedule disappears after a refetch (e.g. got deleted
  // from another tab), drop the selection.
  useEffect(() => {
    if (!selectedId) return;
    const stillExists = allSchedules.some((s) => s.id === selectedId);
    if (!stillExists && !currentQuery.isLoading) {
      setSelectedId(null);
      setSelectedRunId(null);
    }
  }, [selectedId, allSchedules, currentQuery.isLoading]);

  // Dropping run selection if it vanishes from runs list.
  useEffect(() => {
    if (!selectedRunId) return;
    if (!runs.some((r) => r.id === selectedRunId) && !runsQuery.isLoading) {
      setSelectedRunId(null);
    }
  }, [selectedRunId, runs, runsQuery.isLoading]);

  const handleDelete = () => {
    if (!selectedSchedule) return;
    deleteMutation.mutate(
      { scheduleId: selectedSchedule.id },
      {
        onSuccess: () => {
          enqueueSnackbar("Schedule deleted", { variant: "success" });
          setConfirmDeleteOpen(false);
          setSelectedId(null);
          setSelectedRunId(null);
        },
        onError: (err) => {
          enqueueSnackbar(
            `Failed to delete schedule: ${err instanceof Error ? err.message : "Unknown error"}`,
            { variant: "error" },
          );
        },
      },
    );
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="lg"
        PaperProps={{ sx: { height: "min(760px, 92vh)" } }}
      >
        <DialogTitle sx={{ pr: 6, pb: 1 }}>
          <Typography variant="h6" fontWeight={600}>
            Schedules
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Automated Argus sessions for this workspace
          </Typography>
          <IconButton
            aria-label="Close"
            onClick={onClose}
            size="small"
            sx={{ position: "absolute", right: 10, top: 10 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <Box sx={{ px: 3, borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={tab}
            onChange={(_, v) => {
              setTab(v as ArgusScheduleStatus);
              setSelectedId(null);
              setSelectedRunId(null);
              setQuery("");
            }}
            aria-label="Schedule status tabs"
          >
            <Tab
              value="active"
              label={<TabLabel text="Active" count={activeQuery.data ? activeCount : null} />}
            />
            <Tab value="deleted" label={<TabLabel text="Past" count={pastCount} />} />
          </Tabs>
        </Box>

        <DialogContent
          sx={{
            p: 0,
            display: "flex",
            minHeight: CONTENT_MIN_HEIGHT,
            flex: 1,
          }}
        >
          {/* LIST COLUMN */}
          <Box
            sx={{
              width: LIST_WIDTH,
              flexShrink: 0,
              borderRight: 1,
              borderColor: "divider",
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
            }}
          >
            <Box sx={{ p: 1.5, borderBottom: 1, borderColor: "divider" }}>
              <TextField
                fullWidth
                size="small"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Filter schedules…"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ color: "text.secondary" }} />
                    </InputAdornment>
                  ),
                  sx: { fontSize: "0.875rem" },
                }}
              />
            </Box>
            <Box sx={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
              {currentQuery.isLoading && !currentQuery.data ? (
                <LoadingBox />
              ) : currentQuery.isError ? (
                <Alert severity="error" sx={{ m: 2 }}>
                  Failed to load schedules.
                </Alert>
              ) : filteredSchedules.length === 0 ? (
                <EmptyState
                  variant="centered"
                  message={
                    query
                      ? "No matches"
                      : tab === "active"
                        ? "No active schedules"
                        : "No past schedules"
                  }
                  description={
                    query
                      ? `Nothing matches "${query}".`
                      : tab === "active"
                        ? "Schedules you create will appear here."
                        : "One-shots auto-archive after running."
                  }
                  sx={{ py: 6 }}
                />
              ) : (
                <Stack sx={{ py: 0.5 }}>
                  {filteredSchedules.map((s) => (
                    <ScheduleListRow
                      key={s.id}
                      schedule={s}
                      selected={s.id === selectedId}
                      onClick={() => {
                        setSelectedId(s.id);
                        setSelectedRunId(null);
                      }}
                    />
                  ))}
                </Stack>
              )}
            </Box>
          </Box>

          {/* DETAIL COLUMN */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              backgroundColor: "background.default",
            }}
          >
            {!selectedSchedule ? (
              <DetailEmpty />
            ) : selectedRun ? (
              <RunDetail
                schedule={selectedSchedule}
                run={selectedRun}
                onBack={() => setSelectedRunId(null)}
                onNavigateToSession={onNavigateToSession}
              />
            ) : (
              <ScheduleDetail
                schedule={selectedSchedule}
                runs={runs}
                runsLoading={runsQuery.isLoading}
                onSelectRun={setSelectedRunId}
                canDelete={tab === "active"}
                onRequestDelete={() => setConfirmDeleteOpen(true)}
              />
            )}
          </Box>
        </DialogContent>
      </Dialog>

      {/* DELETE CONFIRMATION */}
      <Dialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete schedule?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {selectedSchedule ? (
              <>
                Delete <strong>{selectedSchedule.name}</strong>? Runs already executed will be
                preserved. This cannot be undone.
              </>
            ) : (
              "Delete this schedule? This cannot be undone."
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDelete}
            loading={deleteMutation.isPending}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function TabLabel({ text, count }: { text: string; count: number | null }) {
  return (
    <Stack direction="row" spacing={0.75} alignItems="center">
      <Typography variant="body2" sx={{ textTransform: "none", fontWeight: 500 }}>
        {text}
      </Typography>
      {count !== null && (
        <Box
          sx={{
            px: 0.75,
            py: 0.125,
            borderRadius: 99,
            fontSize: "0.7rem",
            fontWeight: 600,
            backgroundColor: "action.selected",
            color: "text.secondary",
            minWidth: 20,
            textAlign: "center",
          }}
        >
          {count}
        </Box>
      )}
    </Stack>
  );
}

interface ScheduleListRowProps {
  schedule: ArgusSchedule;
  selected: boolean;
  onClick: () => void;
}

function ScheduleListRow({ schedule, selected, onClick }: ScheduleListRowProps) {
  const trigger = scheduleTrigger(schedule);
  const isOrgWide = schedule.instance_id === null;

  return (
    <Box
      onClick={onClick}
      sx={{
        px: 2,
        py: 1.25,
        cursor: "pointer",
        position: "relative",
        borderLeft: 3,
        borderLeftColor: selected ? "primary.main" : "transparent",
        backgroundColor: selected ? "action.selected" : "transparent",
        transition: "background-color 120ms ease",
        "&:hover": {
          backgroundColor: selected ? "action.selected" : "action.hover",
        },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            flexShrink: 0,
            backgroundColor: schedule.enabled ? "success.main" : "text.disabled",
          }}
        />
        <Typography
          variant="body2"
          fontWeight={600}
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
          }}
        >
          {schedule.name}
        </Typography>
      </Stack>
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.75}
        sx={{ mt: 0.5, ml: 2, color: "text.secondary" }}
      >
        {trigger.kind === "cron" ? (
          <ScheduleIcon sx={{ fontSize: 14 }} />
        ) : (
          <EventIcon sx={{ fontSize: 14 }} />
        )}
        <Typography
          variant="caption"
          sx={{
            fontFamily: trigger.kind === "cron" ? "monospace" : undefined,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {trigger.label}
        </Typography>
      </Stack>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.25, ml: 2 }}>
        <Typography variant="caption" color="text.secondary">
          {schedule.last_triggered_at
            ? `Last run ${formatRelative(schedule.last_triggered_at)}`
            : "Never run"}
        </Typography>
        {isOrgWide && (
          <Chip
            label="Org-wide"
            size="small"
            variant="outlined"
            sx={{
              height: 16,
              fontSize: "0.65rem",
              "& .MuiChip-label": { px: 0.75 },
            }}
          />
        )}
      </Stack>
    </Box>
  );
}

function DetailEmpty() {
  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 4,
      }}
    >
      <Stack alignItems="center" spacing={1}>
        <ScheduleIcon sx={{ fontSize: 40, color: "text.disabled" }} />
        <Typography variant="body2" color="text.secondary">
          Select a schedule to see its details
        </Typography>
      </Stack>
    </Box>
  );
}

interface ScheduleDetailProps {
  schedule: ArgusSchedule;
  runs: ArgusScheduleRun[];
  runsLoading: boolean;
  onSelectRun: (runId: string) => void;
  canDelete: boolean;
  onRequestDelete: () => void;
}

function ScheduleDetail({
  schedule,
  runs,
  runsLoading,
  onSelectRun,
  canDelete,
  onRequestDelete,
}: ScheduleDetailProps) {
  const trigger = scheduleTrigger(schedule);

  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
      <Box sx={{ px: 4, pt: 3, pb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="h6" fontWeight={600} sx={{ flex: 1, wordBreak: "break-word" }}>
            {schedule.name}
          </Typography>
          <Chip
            label={schedule.enabled ? "Enabled" : "Disabled"}
            color={schedule.enabled ? "success" : "default"}
            size="small"
            variant={schedule.enabled ? "filled" : "outlined"}
          />
          <Chip
            icon={
              trigger.kind === "cron" ? (
                <ScheduleIcon sx={{ fontSize: 14 }} />
              ) : (
                <EventIcon sx={{ fontSize: 14 }} />
              )
            }
            label={trigger.short}
            size="small"
            variant="outlined"
          />
        </Stack>
        <Stack
          direction="row"
          spacing={2}
          sx={{ mt: 1, color: "text.secondary", flexWrap: "wrap", rowGap: 0.5 }}
        >
          <Typography variant="caption">
            <Box component="span" sx={{ fontWeight: 600, color: "text.secondary" }}>
              {trigger.kind === "cron" ? "Cron " : "Runs at "}
            </Box>
            <Box
              component="span"
              sx={{
                fontFamily: trigger.kind === "cron" ? "monospace" : undefined,
                color: "text.primary",
              }}
            >
              {trigger.label}
            </Box>
          </Typography>
          <Typography variant="caption">
            <Box component="span" sx={{ fontWeight: 600 }}>
              Last run{" "}
            </Box>
            <Box component="span" sx={{ color: "text.primary" }}>
              {formatRelative(schedule.last_triggered_at)}
            </Box>
          </Typography>
          <Typography variant="caption">
            <Box component="span" sx={{ fontWeight: 600 }}>
              Created{" "}
            </Box>
            <Box component="span" sx={{ color: "text.primary" }}>
              {formatAbsolute(schedule.created_at)}
            </Box>
          </Typography>
        </Stack>
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto", px: 4, pb: 3, minHeight: 0 }}>
        <SectionLabel>Prompt</SectionLabel>
        <Box
          sx={{
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            fontSize: "0.8125rem",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            bgcolor: "background.paper",
            border: 1,
            borderColor: "divider",
            p: 2,
            borderRadius: `${RADIUS_MD}px`,
            maxHeight: 220,
            overflowY: "auto",
            color: "text.primary",
          }}
        >
          {schedule.prompt}
        </Box>

        <Box sx={{ mt: 3 }}>
          <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 1 }}>
            <SectionLabel sx={{ mb: 0 }}>Recent runs</SectionLabel>
            {runs.length > 0 && (
              <Typography variant="caption" color="text.secondary">
                ({runs.length})
              </Typography>
            )}
          </Stack>
          {runsLoading && runs.length === 0 ? (
            <LoadingBox />
          ) : runs.length === 0 ? (
            <EmptyState
              variant="compact"
              message="No runs yet"
              description={
                schedule.enabled ? "The next fire will appear here." : "This schedule is disabled."
              }
            />
          ) : (
            <Stack spacing={1}>
              {runs.map((run) => (
                <RunListRow key={run.id} run={run} onClick={() => onSelectRun(run.id)} />
              ))}
            </Stack>
          )}
        </Box>
      </Box>

      {canDelete && (
        <Box
          sx={{
            px: 4,
            py: 2,
            borderTop: 1,
            borderColor: "divider",
            display: "flex",
            justifyContent: "flex-end",
            backgroundColor: "background.paper",
          }}
        >
          <Button
            color="error"
            variant="text"
            size="small"
            startIcon={<DeleteIcon fontSize="small" />}
            onClick={onRequestDelete}
          >
            Delete schedule
          </Button>
        </Box>
      )}
    </Box>
  );
}

interface RunListRowProps {
  run: ArgusScheduleRun;
  onClick: () => void;
}

function RunListRow({ run, onClick }: RunListRowProps) {
  const color = runStatusColor(run.status);
  const hasReport = !!run.report_s3_key;

  return (
    <Box
      onClick={onClick}
      sx={{
        cursor: "pointer",
        border: 1,
        borderColor: "divider",
        borderRadius: `${RADIUS_MD}px`,
        p: 1.5,
        backgroundColor: "background.paper",
        transition: "border-color 120ms ease, box-shadow 120ms ease",
        "&:hover": {
          borderColor: "primary.main",
        },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
        <Chip label={run.status} size="small" color={color} />
        <Chip
          label={run.trigger.replace("_", "-")}
          size="small"
          variant="outlined"
          sx={{ textTransform: "capitalize" }}
        />
        {hasReport && (
          <Tooltip title="Report available">
            <ReportIcon fontSize="small" sx={{ color: "text.secondary" }} />
          </Tooltip>
        )}
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" color="text.secondary">
          {formatAbsolute(run.started_at)} · {runDurationLabel(run)}
        </Typography>
      </Stack>
      {(run.outcome_summary || run.error) && (
        <Typography
          variant="caption"
          color={run.error ? "error" : "text.secondary"}
          sx={{
            mt: 0.75,
            whiteSpace: "pre-wrap",
            overflow: "hidden",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
          }}
        >
          {run.error ?? run.outcome_summary}
        </Typography>
      )}
    </Box>
  );
}

interface RunDetailProps {
  schedule: ArgusSchedule;
  run: ArgusScheduleRun;
  onBack: () => void;
  onNavigateToSession: (sessionId: string) => void;
}

function RunDetail({ schedule, run, onBack, onNavigateToSession }: RunDetailProps) {
  const isLive = run.status.toLowerCase() === "running";
  const {
    reportMd,
    isLoading: reportLoading,
    error: reportError,
  } = useArgusSessionReport(run.argus_session_id ?? undefined, true, isLive);

  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
      <Box sx={{ px: 4, pt: 2.5, pb: 2 }}>
        <Button
          onClick={onBack}
          startIcon={<ArrowBackIcon fontSize="small" />}
          size="small"
          variant="text"
          sx={{ ml: -1, mb: 1, color: "text.secondary" }}
        >
          {schedule.name}
        </Button>

        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
          <Typography variant="h6" fontWeight={600}>
            Run {formatRelative(run.started_at)}
          </Typography>
          <Chip label={run.status} size="small" color={runStatusColor(run.status)} />
          <Chip
            label={run.trigger.replace("_", "-")}
            size="small"
            variant="outlined"
            sx={{ textTransform: "capitalize" }}
          />
        </Stack>
        <Stack
          direction="row"
          spacing={2}
          sx={{ mt: 1, color: "text.secondary", flexWrap: "wrap", rowGap: 0.5 }}
        >
          <Typography variant="caption">
            <Box component="span" sx={{ fontWeight: 600 }}>
              Started{" "}
            </Box>
            {formatAbsolute(run.started_at)}
          </Typography>
          <Typography variant="caption">
            <Box component="span" sx={{ fontWeight: 600 }}>
              Duration{" "}
            </Box>
            {runDurationLabel(run)}
          </Typography>
          {run.cost_usd !== null && (
            <Typography variant="caption">
              <Box component="span" sx={{ fontWeight: 600 }}>
                Cost{" "}
              </Box>
              ${run.cost_usd.toFixed(4)}
            </Typography>
          )}
        </Stack>
      </Box>

      {reportLoading && !reportMd && <LinearProgress />}

      <Box sx={{ flex: 1, overflowY: "auto", px: 4, pb: 3, minHeight: 0 }}>
        {run.error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {run.error}
          </Alert>
        )}

        <SectionLabel>Report</SectionLabel>
        {!run.argus_session_id ? (
          <EmptyState
            variant="compact"
            message="No linked session"
            description="This run didn't produce a session — nothing to report."
          />
        ) : reportError ? (
          <Alert severity="warning">
            Couldn't load report:{" "}
            {reportError instanceof Error ? reportError.message : "unknown error"}
          </Alert>
        ) : reportMd ? (
          <Box
            sx={{
              bgcolor: "background.paper",
              border: 1,
              borderColor: "divider",
              borderRadius: `${RADIUS_MD}px`,
              p: 2.5,
            }}
          >
            <MuiMarkdown overrides={MARKDOWN_OVERRIDES}>{reportMd}</MuiMarkdown>
          </Box>
        ) : !reportLoading ? (
          <EmptyState
            variant="compact"
            message={isLive ? "Report not written yet" : "No report was produced"}
            description={
              isLive
                ? "The agent may write one before the run ends."
                : "This run didn't write a report.md file."
            }
          />
        ) : (
          <LoadingBox />
        )}

        {run.outcome_summary && !reportMd && (
          <Box sx={{ mt: 3 }}>
            <SectionLabel>Outcome summary</SectionLabel>
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
              {run.outcome_summary}
            </Typography>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          px: 4,
          py: 2,
          borderTop: 1,
          borderColor: "divider",
          display: "flex",
          justifyContent: "flex-end",
          gap: 1,
          backgroundColor: "background.paper",
        }}
      >
        {run.argus_session_id ? (
          <Button
            variant="contained"
            size="small"
            endIcon={<OpenInNewIcon fontSize="small" />}
            onClick={() => onNavigateToSession(run.argus_session_id as string)}
          >
            Open session
          </Button>
        ) : (
          <Button variant="contained" size="small" disabled>
            No session
          </Button>
        )}
      </Box>
    </Box>
  );
}

function SectionLabel({ children, sx }: { children: React.ReactNode; sx?: object }) {
  return (
    <Typography
      variant="overline"
      sx={{
        display: "block",
        color: "text.secondary",
        letterSpacing: 0.8,
        mb: 1,
        fontWeight: 600,
        ...sx,
      }}
    >
      {children}
    </Typography>
  );
}
